import React, { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Autocomplete, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButtonGroup, ToggleButton, Divider, Chip, Alert,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useGetDocRepMutation, useSubmitPspMutation } from "state/api";
import CameraUpload from "components/CameraUpload";
import { toast } from "react-toastify";
import endocareLogo from "../../assets/Endocare.png";

const YesNoToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive value={value}
    onChange={(_, v) => { if (v !== null) onChange(v); }}
    size="small"
  >
    <ToggleButton value="yes" sx={{
      px: 3.5, fontWeight: 700,
      "&.Mui-selected": { bgcolor: "#2E7D32", color: "white", "&:hover": { bgcolor: "#1B5E20" } },
    }}>Yes</ToggleButton>
    <ToggleButton value="no" sx={{
      px: 3.5, fontWeight: 700,
      "&.Mui-selected": { bgcolor: "#C62828", color: "white", "&:hover": { bgcolor: "#7B0000" } },
    }}>No</ToggleButton>
  </ToggleButtonGroup>
);

const emptyEntry  = () => ({ doctorName: "", doctCd: "", dateGiven: new Date().toISOString().split("T")[0], patientEnrolled: "no" });
const emptyPatient = () => ({ patientsCount: "", prescription: null, invoice: null });

const PspTracker = () => {
  const { user } = useSelector((s) => s.auth);
  const [getDocRep] = useGetDocRepMutation();
  const [submitPsp, { isLoading }] = useSubmitPspMutation();

  const [doctorOptions, setDoctorOptions] = useState([]);

  /* ── main page state ── */
  const [kitsCount, setKitsCount] = useState("");

  /* ── Dialog 1 (doctor list) ── */
  const [showDoctorDialog, setShowDoctorDialog] = useState(false);
  const [entries, setEntries] = useState([]);

  /* ── Dialog 2 (patient details for one doctor) ── */
  const [patientDialogIdx, setPatientDialogIdx] = useState(null);
  const [tempPatient, setTempPatient] = useState(emptyPatient());

  /* ── saved patient data per doctor index ── */
  const [savedPatients, setSavedPatients] = useState({});

  useEffect(() => {
    if (user?.role) {
      getDocRep({ MgCd: user.role, HqMstId: user.HqMstId })
        .unwrap().then((r) => setDoctorOptions(r.data || [])).catch(console.error);
    }
  }, [user, getDocRep]);

  /* open Dialog 1 */
  const openDoctorDialog = () => {
    const n = parseInt(kitsCount);
    if (!n || n < 1) { toast.error("Enter number of PSP kits given"); return; }
    setEntries(Array.from({ length: n }, (_, i) => entries[i] || emptyEntry()));
    setSavedPatients({});
    setShowDoctorDialog(true);
  };

  const updateEntry = (idx, field, value) => {
    setEntries((prev) => { const a = [...prev]; a[idx] = { ...a[idx], [field]: value }; return a; });
  };

  /* When Yes/No toggled on a doctor row */
  const handlePatientToggle = (idx, value) => {
    updateEntry(idx, "patientEnrolled", value);
    if (value === "yes") {
      /* open Dialog 2 pre-filled with any existing data */
      setTempPatient({ ...(savedPatients[idx] || emptyPatient()) });
      setPatientDialogIdx(idx);
    } else {
      /* clear saved data when switched back to No */
      setSavedPatients((p) => { const n = { ...p }; delete n[idx]; return n; });
    }
  };

  /* "Edit" button on a saved patient row reopens Dialog 2 */
  const editPatient = (idx) => {
    setTempPatient({ ...savedPatients[idx] });
    setPatientDialogIdx(idx);
  };

  /* Save from Dialog 2 → back to Dialog 1 */
  const savePatient = () => {
    if (!tempPatient.patientsCount) { toast.error("No. of Patients Enrolled is required"); return; }
    setSavedPatients((p) => ({ ...p, [patientDialogIdx]: { ...tempPatient } }));
    setPatientDialogIdx(null);
  };

  /* Cancel Dialog 2 — if nothing was saved yet, revert toggle to No */
  const cancelPatientDialog = () => {
    if (!savedPatients[patientDialogIdx]?.patientsCount) {
      updateEntry(patientDialogIdx, "patientEnrolled", "no");
    }
    setPatientDialogIdx(null);
  };

  /* Final submit */
  const handleSubmit = async () => {
    for (let i = 0; i < entries.length; i++) {
      const e = entries[i];
      if (!e.doctorName) { toast.error(`Doctor ${i + 1}: Dr. Name is required`); return; }
      if (!e.dateGiven)  { toast.error(`Doctor ${i + 1}: Date when given is required`); return; }
      if (e.patientEnrolled === "yes" && !savedPatients[i]?.patientsCount) {
        toast.error(`Doctor ${i + 1}: Patient details not saved — click Yes and fill patient info`); return;
      }
    }

    const fd = new FormData();
    fd.append("EmpNo", user.id);
    fd.append("MgCd", user.role);
    fd.append("kitsCount", kitsCount);
    fd.append("doctors", JSON.stringify(
      entries.map((e, i) => ({
        doctorName:      e.doctorName,
        doctCd:          e.doctCd,
        dateGiven:       e.dateGiven,
        patientEnrolled: e.patientEnrolled === "yes",
        patientsCount:   savedPatients[i]?.patientsCount || "0",
      }))
    ));
    entries.forEach((_, i) => {
      if (savedPatients[i]?.prescription) fd.append(`prescription_${i}`, savedPatients[i].prescription);
      if (savedPatients[i]?.invoice)      fd.append(`invoice_${i}`,      savedPatients[i].invoice);
    });

    try {
      await submitPsp(fd).unwrap();
      toast.success("PSP tracker submitted!");
      setKitsCount(""); setEntries([]); setShowDoctorDialog(false); setSavedPatients({});
    } catch (err) {
      toast.error(err?.data?.message || "Submit failed");
    }
  };

  /* ─────────────────────────────────────────── */
  return (
    <Box sx={{ maxWidth: 600, mx: "auto" }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Box component="img" src={endocareLogo} alt="Endocare PSP"
          sx={{ maxHeight: 110, width: "auto", objectFit: "contain" }} />
      </Box>

      <TextField
        fullWidth type="number"
        label="*No. of Dr. PSP Kits Given"
        helperText="Based on No. of Doctors — a window will open to fill details"
        value={kitsCount}
        onChange={(e) => setKitsCount(e.target.value)}
        inputProps={{ min: 1, max: 20 }}
        sx={{ mb: 2 }}
      />

      <Button fullWidth variant="contained" size="large" onClick={openDoctorDialog}
        disabled={!kitsCount || parseInt(kitsCount) < 1}
        sx={{ py: 1.5, bgcolor: "#7B0000", "&:hover": { bgcolor: "#5a0000" } }}>
        Enter Doctor Details →
      </Button>

      {/* ════════════════════════════════════════
          DIALOG 1 — Doctor list
      ════════════════════════════════════════ */}
      <Dialog open={showDoctorDialog} onClose={() => setShowDoctorDialog(false)}
        maxWidth="sm" fullWidth scroll="paper"
        PaperProps={{ sx: { borderRadius: 3 } }}>

        <DialogTitle sx={{ bgcolor: "#7B0000", color: "white", py: 1.5, px: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box component="img" src={endocareLogo} alt=""
              sx={{ height: 36, objectFit: "contain", filter: "brightness(0) invert(1)" }} />
            <Box>
              <Typography fontWeight={700} fontSize="1rem">Endocare-PSP Tracker</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {kitsCount} kit{parseInt(kitsCount) !== 1 ? "s" : ""} — fill details for each doctor
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          {entries.map((entry, i) => (
            <Box key={i}>
              <Chip label={`Doctor ${i + 1}`} size="small"
                sx={{ mb: 1.5, bgcolor: "#7B0000", color: "white", fontWeight: 700 }} />

              <Autocomplete
                options={doctorOptions}
                getOptionLabel={(opt) => `${opt.DoctName} (${opt.DoctCd || "N/A"})`}
                onChange={(_, v) => {
                  updateEntry(i, "doctorName", v?.DoctName || "");
                  updateEntry(i, "doctCd", v?.DoctCd || "");
                }}
                renderInput={(params) => (
                  <TextField {...params} label="*Dr. Name" size="small" sx={{ mb: 1.5 }} required />
                )}
              />

              <TextField fullWidth type="date" label="*Date when given"
                value={entry.dateGiven}
                onChange={(e) => updateEntry(i, "dateGiven", e.target.value)}
                InputLabelProps={{ shrink: true }} size="small" sx={{ mb: 1.5 }} required />

              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                flexWrap: "wrap", gap: 1,
                p: 1.5, border: "1px dashed #ccc", borderRadius: 2, mb: 1,
              }}>
                <Typography variant="body2" fontWeight={500}>* Any Patient Enrolled</Typography>
                <YesNoToggle value={entry.patientEnrolled}
                  onChange={(v) => handlePatientToggle(i, v)} />
              </Box>

              {/* Show saved patient summary with Edit button */}
              {entry.patientEnrolled === "yes" && savedPatients[i]?.patientsCount && (
                <Alert severity="success" icon={<CheckCircle fontSize="small" />}
                  action={
                    <Button size="small" color="inherit" onClick={() => editPatient(i)}>
                      Edit
                    </Button>
                  }
                  sx={{ mb: 1, py: 0.5 }}>
                  {savedPatients[i].patientsCount} patient{savedPatients[i].patientsCount > 1 ? "s" : ""} enrolled
                  {savedPatients[i].prescription ? " · Rx ✓" : ""}
                  {savedPatients[i].invoice      ? " · Invoice ✓" : ""}
                </Alert>
              )}

              {/* Prompt to fill if toggled Yes but not yet saved */}
              {entry.patientEnrolled === "yes" && !savedPatients[i]?.patientsCount && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
                  Tap <strong>Yes</strong> again or{" "}
                  <Button size="small" color="inherit" onClick={() => editPatient(i)}>
                    fill patient details
                  </Button>
                </Alert>
              )}

              {i < entries.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setShowDoctorDialog(false)}
            sx={{ color: "#7B0000", borderColor: "#7B0000" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isLoading}
            sx={{ flex: 1, py: 1, bgcolor: "#7B0000", "&:hover": { bgcolor: "#5a0000" } }}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Submit All"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ════════════════════════════════════════
          DIALOG 2 — Patient details (nested)
          Opens when "Yes" is clicked in Dialog 1
      ════════════════════════════════════════ */}
      <Dialog open={patientDialogIdx !== null} onClose={cancelPatientDialog}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>

        <DialogTitle sx={{ bgcolor: "#C62828", color: "white", py: 1.5, px: 2 }}>
          <Typography fontWeight={700} fontSize="1rem">Patient Enrollment Details</Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            {patientDialogIdx !== null
              ? `Doctor ${patientDialogIdx + 1}${entries[patientDialogIdx]?.doctorName ? ` — ${entries[patientDialogIdx].doctorName}` : ""}`
              : ""}
          </Typography>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2 }}>
          <TextField
            fullWidth type="number"
            label="*No. of Patients Enrolled"
            value={tempPatient.patientsCount}
            onChange={(e) => setTempPatient((p) => ({ ...p, patientsCount: e.target.value }))}
            inputProps={{ min: 1 }} size="small" sx={{ mb: 2 }} required autoFocus
          />
          <CameraUpload
            label="*Upload Prescriptions"
            onFileSelect={(f) => setTempPatient((p) => ({ ...p, prescription: f }))}
          />
          <CameraUpload
            label="*Upload Invoices"
            onFileSelect={(f) => setTempPatient((p) => ({ ...p, invoice: f }))}
            accept="image/*,.pdf"
          />
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={cancelPatientDialog}
            sx={{ color: "#C62828", borderColor: "#C62828" }}>Cancel</Button>
          <Button variant="contained" onClick={savePatient}
            sx={{ flex: 1, bgcolor: "#C62828", "&:hover": { bgcolor: "#7B0000" } }}>
            Save &amp; Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="body2" sx={{ mt: 3, textAlign: "center", color: "text.secondary" }}>
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>Note: </Box>
        Sections marked with{" "}
        <Box component="span" sx={{ color: "#C41E3A", fontWeight: 700 }}>*</Box>
        {" "}are mandatory for the submission
      </Typography>
    </Box>
  );
};

export default PspTracker;
